import { useEffect } from 'react'
import { OtherPrivateEventsAvailability } from '@/components/AlternativeAvailability'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import { GFL_ALLOWED_TAGS, HTMLContent } from '@sevenrooms/ui-kit/core'
import { Button } from '@sevenrooms/ui-kit/form'
import { VStack } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { type AvailabilityTimeWithUpSellCost, useLanguageStrings, useWidgetSettings } from '../../hooks'
import { PrivateEventsBanner } from '../../Reservations/Search/PrivateEventsBanner'
import { reservationWidgetMessages } from '../../reservationWidgetMessages'
import { useModals } from '../../store'
import { TimeFilterButton } from '../TimeFilterButton'
import { WidgetTitle } from '../WidgetTitle'
import { RequestOrPriorityAlertAvailability } from './RequestOrPriorityAlertAvailability'
import { ReservationAvailability, type ReservationAvailabilityProps } from './ReservationAvailability'
import { SpecialAttention } from './SpecialAttention'
import type { ExperienceWithAvailability } from '../../hooks/useAvailability'

export interface PrimaryAvailabilityProps {
  startDate: string
  partySize: number
  onClick: ReservationAvailabilityProps['onClick']
  onPrivateEventsTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability) => void
  onPrivateEventsMoreDetailsClick?: (experience: ExperienceWithAvailability) => void
  onPrivateEventsExploreClick?: () => void
  venueCurrencyCode: string
  onClearTimeFilter: () => void
  isDayBlocked: (day: Date) => boolean
  reservationTimesInHalo: ReservationAvailabilityProps['timeslots']
  experiences?: ExperienceWithAvailability[]
  requestTimeRange: string[]
  reservationTimes: ReservationAvailabilityProps['timeslots']
  showPrivateEventsBanner: boolean
  isAvailabilityFetching: boolean
  isClosed: boolean
  errorCode?: number | undefined
}

export function PrimaryAvailability({
  startDate,
  partySize,
  onClick,
  onPrivateEventsTimeSlotClick,
  onPrivateEventsMoreDetailsClick,
  onPrivateEventsExploreClick,
  venueCurrencyCode,
  onClearTimeFilter,
  isDayBlocked,
  experiences,
  reservationTimesInHalo,
  requestTimeRange,
  reservationTimes,
  isAvailabilityFetching,
  isClosed,
  showPrivateEventsBanner,
  errorCode,
}: PrimaryAvailabilityProps) {
  const { showSpecialAttentionMessage, privateEventsTab } = useWidgetSettings()
  const { formatMessage } = useLocales()

  const { isFetching: isFetchingLanguage } = useLanguageStrings()

  const { showErrorModal } = useModals()

  const timesOutSideOfHalo = reservationTimes.length - reservationTimesInHalo.length

  useEffect(() => {
    if (errorCode !== undefined) {
      showErrorModal()
    }
  }, [errorCode, showErrorModal, formatMessage])

  const noAvailabilityTitle = () => {
    if (isClosed) {
      return formatMessage(reservationWidgetMessages.resWidgetNoShiftsOnDate, {
        date: DateOnly.from(startDate).formatSMonthNDaySWeek(),
      })
    }

    if (isDayBlocked(DateOnly.from(startDate).toJsDate())) {
      return formatMessage(reservationWidgetMessages.resWidgetBlackoutDate)
    }

    const noAvailabilityMessage =
      !!experiences?.length && !reservationTimesInHalo.length
        ? formatMessage(reservationWidgetMessages.resWidgetPrivateEventsNoAvailabilityGroupBookingLabel)
        : formatMessage(reservationWidgetMessages.resWidgetNoAvailabilityCriteria)

    return (
      <>
        <HTMLContent allowedTags={GFL_ALLOWED_TAGS} content={noAvailabilityMessage} />
        {reservationTimesInHalo.length === 0 && reservationTimes.length ? (
          <TimeFilterButton date={startDate} onClick={onClearTimeFilter} />
        ) : null}
      </>
    )
  }

  const bannerImgUrl = privateEventsTab?.bannerImgUrl ?? null

  return (
    <VStack width="100%" pt="lm" pb="lm" spacing="lm">
      <WidgetTitle />
      {showSpecialAttentionMessage && <SpecialAttention data-test="venue-special-attention" />}
      {!reservationTimesInHalo.length && (
        <OtherPrivateEventsAvailability
          startDate={startDate}
          partySize={partySize}
          experiences={experiences}
          onTimeSlotClick={onPrivateEventsTimeSlotClick}
          onMoreDetailsClick={onPrivateEventsMoreDetailsClick}
          direction="row"
          timeSlotVariant="primary"
        />
      )}
      <ReservationAvailability
        isLoadingData={isAvailabilityFetching}
        isLoadingLanguage={isFetchingLanguage}
        onClick={onClick}
        timeslots={reservationTimesInHalo}
        variant="primary"
        noAvailabilityTitle={noAvailabilityTitle()}
        currencyCode={venueCurrencyCode}
        requestOrPriorityAlertAvailability={
          requestTimeRange.length ? (
            <RequestOrPriorityAlertAvailability
              requestTimeSlots={requestTimeRange}
              variant={reservationTimesInHalo.length === 0 ? 'primary' : 'secondary'}
              partySize={partySize}
              startDate={startDate}
            />
          ) : null
        }
      />
      {!!timesOutSideOfHalo && reservationTimesInHalo.length !== 0 && (
        <div>
          <Button data-test="sr-show-x-more-times" type="button" variant="tertiary" noPadding onClick={onClearTimeFilter}>
            <Text color="primaryFont" textDecoration="underline">
              {formatMessage(reservationWidgetMessages.resWidgetShowXMoreTimes, {
                number_of_times: timesOutSideOfHalo,
                date: DateOnly.from(startDate).formatSMonthNDaySWeek(),
              })}
            </Text>
          </Button>
        </div>
      )}
      {!!reservationTimesInHalo.length && (
        <OtherPrivateEventsAvailability
          startDate={startDate}
          partySize={partySize}
          experiences={experiences}
          onTimeSlotClick={onPrivateEventsTimeSlotClick}
          onMoreDetailsClick={onPrivateEventsMoreDetailsClick}
          direction="row"
          timeSlotVariant="primary"
        />
      )}
      {showPrivateEventsBanner && <PrivateEventsBanner bannerImgUrl={bannerImgUrl} onExploreClick={onPrivateEventsExploreClick} />}
    </VStack>
  )
}
